<template>
  <v-container>
    <v-alert type="error" v-if="errorMessage">{{ errorMessage }}</v-alert>
    <v-form @submit.prevent="submitEvent">
      <v-col cols="12" sm="6" md="6" lg="6" style="margin: 0px auto">
        <h2>{{ $t('event_create.title') }}</h2>
        <v-text-field v-model="eventForm.event_name" :label="$t('event_create.event_name_label')" :rules="eventNameRule" maxlength="50" counter />
        <v-text-field v-model="eventForm.event_date_start" :label="$t('event_create.start_date_label')" type="date" />
        <v-text-field v-model="eventForm.event_date_end" :label="$t('event_create.end_date_label')" type="date" />
        <v-select v-model="eventForm.event_type" :items="eventTypes" :label="$t('event_create.event_type_label')" :rules="eventTypeRule" />
        <v-tooltip location="top center" origin="auto" :text="$t('event_create.visibility_help')">
          <template v-slot:activator="{ props }">
            <v-select v-bind="props" v-model="eventForm.visibility" :items="visibilityTypes" :label="$t('event_create.visibility_label')" :rules="visibilityType" />
          </template>
        </v-tooltip>
        <v-btn color="success" type="submit" style="float: right;">{{ $t('event_create.create_btn') }}</v-btn>
        <v-btn color="primary" @click="addRace">{{ $t('event_create.add_race_btn') }}</v-btn>

      </v-col>

      <v-row>
      <v-col cols="12" sm="6" md="4" lg="3" v-for="(race, index) in races" :key="index">
        <v-card>
          <v-card-title>
            {{ $t('event_create.race_card_title', { n: index + 1 }) }}
            <v-btn small color="error" @click="removeRace(index)" style="float: right;">{{ $t('event_create.remove_race_btn') }}</v-btn>
          </v-card-title>
          <v-card-text>
            <v-text-field v-model="race.race_name" :label="$t('event_create.race_name_label')" :rules="eventNameRule" maxlength="40" counter></v-text-field>
            <v-text-field v-model="race.race_date" :label="$t('event_create.race_date_label')" type="date" required></v-text-field>
            <v-select v-model="race.race_level" :items="raceLevelOptions" :label="$t('event_create.race_level_label')" :rules="eventTypeRule" no-data-text="event_create.race_level_no_data"></v-select>
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>

    </v-form>
    
  </v-container>
</template>


<script setup>
import { ref, reactive, computed, watch } from 'vue';
import apiClient from '/api.js';
import { useRouter } from 'vue-router';
import {useSnackbarStore} from '@/stores/snackbarStore'
import { RACE_LEVEL_MAPPING } from '@/utils/raceLevelOptions';
import { EventTypes } from '@/utils/eventTypes'
import { useI18n } from 'vue-i18n'

const { t } = useI18n();

const snackbar = useSnackbarStore()
const router = useRouter();
 

const eventNameRule = ref([v => !!v || t('rules.required')]);

const eventForm = reactive({
  event_name: '',
  event_date_start: '',
  event_date_end: '',
  event_type: '',
  visibility: '',
});


const { eventTypes, eventTypeRule } = EventTypes();



const visibilityType = ref([v => !!v || t('rules.required')]);

const visibilityTypes = computed(() => ([
  { title: t('event_create.visibility_public'), value: 'PUBLIC' },
  { title: t('event_create.visibility_club'), value: 'CLUB' },
  ]));


const races = ref([]);



const errorMessage = ref('');

function addRace() {
  races.value.push({ race_name: '', race_date: '', race_level: '' });
}

// přeložené položky pro select levelů podle vybraného event_type
const raceLevelOptions = computed(() =>
  (RACE_LEVEL_MAPPING[eventForm.event_type] || []).map(code => ({
    value: code,
    title: t(`enums.race_level.${code}`),
  }))
)

watch(() => eventForm.event_type, () => {
  const allowed = new Set(RACE_LEVEL_MAPPING[eventForm.event_type] || [])
  races.value.forEach(r => { if (!allowed.has(r.race_level)) r.race_level = '' })
})

function removeRace(index) {
  races.value.splice(index, 1);
}

async function submitEvent() {
  errorMessage.value = ''; // Reset error message před voláním API

  const startDate = new Date(eventForm.event_date_start);
  const endDate = new Date(eventForm.event_date_end);

   // Kontrola, zda datum začátku je před datem konce události
  if (startDate > endDate) {
    errorMessage.value = t('errors.start_before_end');
    return;
  }

  // Kontrola, zda je vybrán první typ eventu a vytváří se nějaké závody
  if (eventForm.event_type === 'races' && races.value.length === 0) {
    errorMessage.value = t('errors.must_add_stage_for_races');
    return;
  }

  // Kontrola, zda datum závodu je v rozmezí dat začátku a konce události
  for (let race of races.value) {
    const raceDate = new Date(race.race_date);
    if (raceDate < startDate || raceDate > endDate) {
      errorMessage.value = t('errors.stage_date_out_of_range');
      return;
    }
  }

  const autoCreatedRace = {
    race_name: eventForm.event_name, // Použijte název události jako název závodu
    race_date: eventForm.event_date_start, // Použijte datum začátku události jako datum závodu
    race_level: "OTHER", // Můžete nastavit typ závodu stejně jako typ události nebo jinou logiku
  };

  try {
    const eventData = {
      ...eventForm,
      races: [autoCreatedRace, ...races.value],
    };
    const response = await apiClient.post('event/create/', eventData);
    console.log(response.data);
    snackbar.success(t('event_create.created'));
    // Přesměrovat uživatele na stránku s detaily nově vytvořené události
    if (response.data && response.data.id) {
      router.push(`/soutez/${response.data.id}`);
    }
  } catch (error) {
    window.scrollTo({ top: 0, behavior: 'smooth' }); // Scrollování nahoru pro zobrazení chybové zprávy
    if (error.response) {
      // pokud je v response data.detail, použij ho, jinak celý payload
      errorMessage.value = error.response.data.detail 
        || JSON.stringify(error.response.data)
    } else {
      errorMessage.value = t('event_create.create_failed');
    }
  }
}
</script>